import React from 'react';

export default function ApplicationServicesExplanation() {
    const [selectedSection, setSelectedSection] = React.useState('definition');

    return (
        <div className="p-6 max-w-6xl mx-auto">
            <div className="grid grid-cols-2 gap-6">
                <div className="space-y-4">
                    <h2 className="text-2xl font-bold mb-4">Application Services</h2>
                    
                    <div className="mb-4 flex space-x-2">
                        {['definition', 'structure', 'examples', 'implementation'].map(section => (
                            <button
                                key={section}
                                onClick={() => setSelectedSection(section)}
                                className={`px-4 py-2 rounded ${
                                    selectedSection === section
                                        ? 'bg-blue-500 text-white'
                                        : 'bg-gray-100'
                                }`}
                            >
                                {section.charAt(0).toUpperCase() + section.slice(1)}
                            </button>
                        ))}
                    </div>

                    {selectedSection === 'definition' && (
                        <div className="bg-white rounded-lg p-4 shadow">
                            <h3 className="text-lg font-semibold mb-2">Services Applicatifs</h3>
                            <ul className="space-y-2 list-disc list-inside text-gray-700">
                                <li>Orchestrent les cas d'utilisation</li>
                                <li>Coordonnent les services du domaine</li>
                                <li>Gèrent les transactions</li>
                                <li>Assurent la cohérence des données</li>
                                <li>Implémentent les ports entrants</li>
                            </ul>
                        </div>
                    )}

                    {selectedSection === 'structure' && (
                        <div className="bg-white rounded-lg p-4 shadow">
                            <h3 className="text-lg font-semibold mb-2">Structure Type</h3>
                            <pre className="bg-gray-50 p-4 rounded text-sm">
{`@Service
@Transactional
public class PaymentApplicationService implements PaymentOperationsPort {
    private final PaymentDomainService domainService;
    private final PaymentRepository paymentRepository;
    private final PaymentGatewayPort gatewayPort;
    private final NotificationPort notificationPort;

    @Override
    public PaymentResult processPayment(ProcessPaymentCommand command) {
        try {
            // 1. Validation de la commande
            validateCommand(command);

            // 2. Récupération du paiement
            Payment payment = paymentRepository
                .findById(command.paymentId())
                .orElseThrow(() -> new PaymentNotFoundException(command.paymentId()));

            // 3. Validation domaine
            domainService.validateProcessing(payment);

            // 4. Traitement
            GatewayResponse response = gatewayPort.processPayment(
                PaymentRequest.from(payment)
            );

            // 5. Mise à jour du statut
            payment.updateStatus(mapStatus(response.getStatus()));
            payment = paymentRepository.save(payment);

            // 6. Notification
            notificationPort.notifyPaymentStatus(
                PaymentStatusNotification.from(payment)
            );

            return PaymentResult.success(payment);

        } catch (ValidationException e) {
            return PaymentResult.validationError(e.getErrors());
        } catch (BusinessException e) {
            return PaymentResult.businessError(e);
        } catch (Exception e) {
            return PaymentResult.technicalError(e);
        }
    }

    private void validateCommand(ProcessPaymentCommand command) {
        // Validation des données d'entrée
    }

    private PaymentStatus mapStatus(GatewayStatus status) {
        // Mapping des statuts
    }
}`}
                            </pre>
                        </div>
                    )}

                    {selectedSection === 'examples' && (
                        <div className="bg-white rounded-lg p-4 shadow space-y-4">
                            <h3 className="text-lg font-semibold mb-2">Exemples Concrets</h3>
                            
                            <div className="p-2 bg-gray-50 rounded">
                                <h4 className="font-medium text-blue-600">Gestion des Mandats</h4>
                                <pre className="text-xs mt-2">
{`@Service
@Transactional
public class MandateApplicationService implements MandateOperationsPort {
    private final MandateDomainService domainService;
    private final MandateRepository mandateRepository;
    private final CustomerServicePort customerService;
    private final NotificationPort notificationPort;

    @Override
    public MandateResult createMandate(CreateMandateCommand cmd) {
        try {
            // 1. Vérification du client
            CustomerInfo customer = customerService
                .getCustomerInfo(cmd.customerId());

            // 2. Création du mandat
            Mandate mandate = Mandate.builder()
                .reference(generateReference())
                .debtor(customer.toParty())
                .creditor(cmd.creditor())
                .scheme(cmd.scheme())
                .type(cmd.type())
                .build();

            // 3. Validation domaine
            domainService.validateMandate(mandate);

            // 4. Persistance
            mandate = mandateRepository.save(mandate);

            // 5. Notification
            notificationPort.notifyMandateCreation(
                MandateNotification.from(mandate)
            );

            return MandateResult.success(mandate);

        } catch (Exception e) {
            return handleError(e);
        }
    }

    private MandateResult handleError(Exception e) {
        if (e instanceof ValidationException) {
            return MandateResult.validationError(
                ((ValidationException) e).getErrors()
            );
        }
        // Autres cas d'erreur...
        return MandateResult.technicalError(e);
    }
}`}
                                </pre>
                            </div>

                            <div className="p-2 bg-gray-50 rounded">
                                <h4 className="font-medium text-blue-600">Traitement des Messages</h4>
                                <pre className="text-xs mt-2">
{`@Service
@Transactional
public class MessageProcessingService implements MessageProcessingPort {
    private final MessageValidationService validationService;
    private final MessageEnrichmentService enrichmentService;
    private final MessageRoutingService routingService;
    private final MessageRepository messageRepository;

    @Override
    public ProcessingResult processMessage(ProcessMessageCommand cmd) {
        try {
            // 1. Validation du message
            Message message = messageRepository
                .findById(cmd.messageId())
                .orElseThrow();

            ValidationResult validation = 
                validationService.validateMessage(message);

            if (!validation.isValid()) {
                return ProcessingResult.invalid(validation);
            }

            // 2. Enrichissement
            message = enrichmentService.enrichMessage(message);

            // 3. Détermination de la route
            RoutingDecision routing = 
                routingService.determineRoute(message);

            // 4. Envoi
            message.setRouting(routing);
            messageRepository.save(message);

            return ProcessingResult.success(message);

        } catch (Exception e) {
            return ProcessingResult.failure(e);
        }
    }
}`}
                                </pre>
                            </div>
                        </div>
                    )}

                    {selectedSection === 'implementation' && (
                        <div className="bg-white rounded-lg p-4 shadow space-y-4">
                            <h3 className="text-lg font-semibold mb-2">Patterns d'Implémentation</h3>
                            
                            <div className="space-y-2">
                                <h4 className="font-medium">1. Gestion des Transactions</h4>
                                <pre className="bg-gray-50 p-2 rounded text-xs">
{`@Service
public class TransactionalService {
    @Transactional
    public Result<Payment> processWithTransaction(Command cmd) {
        try {
            // Opérations dans la transaction
            return Result.success(payment);
        } catch (Exception e) {
            TransactionAspectSupport.currentTransactionStatus()
                .setRollbackOnly();
            return Result.failure(e);
        }
    }
}`}
                                </pre>
                            </div>

                            <div className="space-y-2">
                                <h4 className="font-medium">2. Event Publishing</h4>
                                <pre className="bg-gray-50 p-2 rounded text-xs">
{`@Service
public class EventPublishingService {
    private final ApplicationEventPublisher eventPublisher;

    public Result<Payment> processWithEvents(Command cmd) {
        try {
            // Traitement
            Payment payment = processPayment(cmd);

            // Publication des événements
            payment.getDomainEvents().forEach(event -> 
                eventPublisher.publishEvent(event)
            );

            return Result.success(payment);
        } catch (Exception e) {
            return Result.failure(e);
        }
    }
}`}
                                </pre>
                            </div>
                        </div>
                    )}
                </div>

                <div className="bg-white rounded-lg p-4 shadow">
                    <h3 className="text-xl font-bold mb-4">Bonnes Pratiques</h3>
                    
                    <div className="space-y-4">
                        <div className="border-l-4 border-blue-500 pl-4">
                            <h4 className="font-semibold">1. Transaction Management</h4>
                            <p className="text-sm text-gray-600">
                                Gérer correctement les transactions et le rollback
                            </p>
                            <code className="text-xs bg-gray-100 p-1 mt-1 block">
                                @Transactional, try-catch with rollback
                            </code>
                        </div>

                        <div className="border-l-4 border-green-500 pl-4">
                            <h4 className="font-semibold">2. Error Handling</h4>
                            <p className="text-sm text-gray-600">
                                Gérer tous les types d'erreurs de manière cohérente
                            </p>
                            <code className="text-xs bg-gray-100 p-1 mt-1 block">
                                Result pattern, Exception handling
                            </code>
                        </div>

                        <div className="border-l-4 border-purple-500 pl-4">
                            <h4 className="font-semibold">3. Event Publishing</h4>
                            <p className="text-sm text-gray-600">
                                Publier les événements applicatifs appropriés
                            </p>
                            <code className="text-xs bg-gray-100 p-1 mt-1 block">
                                ApplicationEventPublisher, Domain Events
                            </code>
                        </div>

                        <div className="border-l-4 border-yellow-500 pl-4">
                            <h4 className="font-semibold">4. Validation</h4>
                            <p className="text-sm text-gray-600">
                                Valider les entrées avant traitement
                            </p>
                            <code className="text-xs bg-gray-100 p-1 mt-1 block">
                                Input validation, Business rules
                            </code>
                        </div>

                        <div className="border-l-4 border-red-500 pl-4">
                            <h4 className="font-semibold">5. Logging</h4>
                            <p className="text-sm text-gray-600">
                                Tracer les opérations importantes
                            </p>
                            <code className="text-xs bg-gray-100 p-1 mt-1 block">
                                SLF4J, Structured logging
                            </code>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}